<script setup>
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import MultiSelect from '../ui/MultiSelect.vue';
import { ElButton, ElDialog, ElMessage } from 'element-plus';
import axios, { HttpStatusCode } from 'axios';

const props = defineProps({
    product: Object,
    toolReference: String,
    mappings: Array,
    service: String,
});

const queryClient = useQueryClient();

const isIntellifloService = computed(() => props.service === 'INTELLIFLO');
const availableFilter = ref('');
const showDialog = ref(false);

const productsQueryKey = computed(() => (
    isIntellifloService.value
        ? ['pricing-products', props.service, props.toolReference, props.product?.providerName, availableFilter.value]
        : ['pricing-products', props.service, props.toolReference]
));

const productsQueryEnabled = computed(() => (
    isIntellifloService.value ? showDialog.value : true
));

function getProductsUrl() {
    const baseUrl = `api/Configuration/ListPricingProducts/${props.service}`;
    // Intelliflo is provider-scoped and supports server-side name filtering.
    if (isIntellifloService.value) {
        return `${baseUrl}/${encodeURIComponent(props.toolReference || '')}/${encodeURIComponent(props.product?.providerName || '')}?filter=${encodeURIComponent(availableFilter.value || '')}`;
    }
    return `${baseUrl}/${encodeURIComponent(props.toolReference || '')}`;
}

const {
    data: products,
} = useQuery({
    queryKey: productsQueryKey,
    enabled: productsQueryEnabled,
    queryFn: () => axios(getProductsUrl()).then(res => res.data),
});


const options = computed(() => {
    const currentProviderKey = String(props.product?.providerReference || '').trim().toLowerCase();
    const mappedProducts = new Set(
        (props.mappings || [])
            .filter(m => String(m.apiProductId || '').trim().toLowerCase() !== currentProviderKey)
            .flatMap(m => m.externalIds || [])
            .map(id => String(id))
    );
    return products.value?.map(product => {
        const key = String(product.productId);
        return {
            key,
            label: product.productId + " - " + product.productName,
            disabled: mappedProducts.has(key),
        };
    }) || []
})

const mapping = computed(() => props.mappings?.find(m => m.apiProductId == props.product.providerReference));
const value = ref(mapping?.value?.externalIds || []);
const mapCount = computed(() => mapping.value?.externalIds?.length || 0);

watch(() => mapping.value, (newValue) => {
    const next = newValue?.externalIds || [];
    value.value = isIntellifloService.value ? next.slice(0, 1) : next;
})

watch(showDialog, (isOpen) => {
    if (isOpen && isIntellifloService.value) {
        availableFilter.value = '';
    }
});

const { mutate, isLoading: isSaving } = useMutation({
    mutationFn: async (request) => {
        try {
            const response = await axios.post('api/Configuration/SaveProductMapping', JSON.stringify(request), {
                headers: {
                    'Accept': 'application/json, text/plain',
                    'Content-Type': 'application/json;charset=UTF-8',
                },
            });

            if (response.status === HttpStatusCode.Ok) {
                return response.data;
            } else {
                throw new Error(response.statusText);
            }
        } catch (error) {
            throw new Error(error.message);
        }
    },
    onSuccess: () => {
        ElMessage.success('Mapping saved.');
        queryClient.invalidateQueries({ queryKey: ['tool-mappings', props.service, props.toolReference] });
        showDialog.value = false;
    },
    onError: () => {
        ElMessage.error('Mapping could not be saved');
    }
});

function saveMappings() {
    mutate({
        serviceType: props.service,
        apiProductId: props.product.providerReference,
        toolReference: props.toolReference,
        externalIds: isIntellifloService.value ? value.value.slice(0, 1) : value.value,
    });
}

const leftTitle = computed(() => {
    if (isIntellifloService.value) {
        return 'Intelliflo products';
    }
    if (props.service === 'IPIPELINE') {
        return 'iPipeline products';
    }
    return 'Products';
});

const serviceLabel = computed(() => {
    if (isIntellifloService.value) {
        return 'Intelliflo';
    }
    if (props.service === 'IPIPELINE') {
        return 'iPipeline';
    }
    return 'the integration';
});

function handleAvailableFilterChange(value) {
    if (!isIntellifloService.value) {
        return;
    }
    availableFilter.value = value || '';
}

function handleMaxReached() {
    if (!isIntellifloService.value) {
        return;
    }
    ElMessage.warning('Only one product can be mapped for Intelliflo.');
}

const titles = computed(() => ({ left: leftTitle.value, right: props.product.providerName }));
</script>
<template>
    <div class="row">
        <div class="title">{{ product.providerName }}</div>
        <div class="subtitle">
            <span>{{ product.providerReference }}</span>
            <span class="mapping" @click="showDialog = true">{{ mapCount }} {{ mapCount == 1 ? 'product' : 'products' }}
                mapped</span>
        </div>
    </div>
    <ElDialog v-model="showDialog" destroy-on-close>
        <p class="tip">Select the products from {{ serviceLabel }} to be mapped to <strong>{{ product.providerName }}</strong></p>
        <MultiSelect
            v-model="value"
            :data="options"
            :titles="titles"
            :max-selected="isIntellifloService ? 1 : null"
            @filter-available-change="handleAvailableFilterChange"
            @max-reached="handleMaxReached"
        />
        <template #footer>
            <div class="dialog-footer">
                <ElButton type="primary" link @click="showDialog = false">Cancel</ElButton>
                <ElButton type="primary" :loading="isSaving" @click="saveMappings">Save and close</ElButton>
            </div>
        </template>
    </ElDialog>
</template>
<style scoped>
.row {
    max-width: 100%;
    padding: 0.5rem;
    border-radius: 0.5rem;
}

.title {
    font-weight: bold;
}

.subtitle {
    display: flex;
    font-size: small;
    gap: 1rem
}

.subtitle .mapping {
    color: var(--el-color-primary);
    text-decoration: underline;
    cursor: pointer;
}

.dialog-footer {
    display: inline-flex;
    gap: 2rem;
}

.dialog-footer button {
    width: unset;
}
</style>
